<template>
  <div class="tier-fact-timeline-2-6-container">
    <FiltersWrapper>
      <CollapseBlock label="Фильтры" :alwaysHideExpand="true" className="tier26-filters-collapse">
        <div class="filters-content">
          <div class="filter-row">
            <CustomSelect
              label="Жилой комплекс"
              :value="filters.resident_id"
              @change="onResidentChange"
              :options="residents"
              optionValueKey="resident_id"
              optionLabelKey="resident_name"
              :loading="residentsFetching"
              :hideInputOnSelect="false"
            />
            <q-btn
              v-if="filters.resident_id"
              flat
              dense
              round
              icon="assignment"
              color="primary"
              @click="openReasonReport"
              class="reason-report-icon-btn"
              title="Отчет по причинам отставания"
            >
              <q-tooltip>Отчет по причинам отставания</q-tooltip>
            </q-btn>
          </div>
        </div>
      </CollapseBlock>
    </FiltersWrapper>

    <div class="content">
      <div class="tabs-row">
        <q-tabs
          v-model="activeTier"
          dense
          class="tier26-tabs"
          active-color="primary"
          indicator-color="transparent"
          align="left"
          narrow-indicator
        >
          <q-tab
            v-for="tier in tiers"
            :key="tier"
            :name="tier"
            :label="`Tier ${tier}`"
            class="tier26-tab"
          />
        </q-tabs>
      </div>

      <q-separator />

      <q-tab-panels v-model="activeTier" animated>
        <q-tab-panel :name="2">
          <div class="tier2-toolbar">
              <div class="tier2-toolbar-left">
                <q-btn
                  flat
                  dense
                  round
                  icon="chevron_left"
                  size="md"
                  @click="tier2Shift(-1)"
                  class="nav-btn"
                  title="Предыдущий период"
                  :disable="!filters.resident_id"
                />
              </div>

              <div class="tier2-title">
                <div class="tier2-title-main">Tier 2</div>
                <div class="tier2-title-sub">
                  {{ tier2MonthLabel }}
                </div>
              </div>

              <div class="tier2-toolbar-right">
                <q-btn
                  flat
                  dense
                  round
                  icon="refresh"
                  size="md"
                  @click="loadTier2Data"
                  class="nav-btn"
                  title="Обновить данные"
                  :disable="!filters.resident_id || blocksFetching"
                  :loading="blocksFetching"
                />
                <q-btn
                  flat
                  dense
                  round
                  icon="chevron_right"
                  size="md"
                  @click="tier2Shift(1)"
                  class="nav-btn"
                  title="Следующий период"
                  :disable="!filters.resident_id"
                />
              </div>
          </div>

          <div v-if="!filters.resident_id" class="no-data">
            Выберите жилой комплекс для отображения данных
          </div>

          <div v-else class="tier2-table-wrapper">
            <table class="tier2-table">
              <thead>
                <tr>
                  <th class="sticky-col head-constructive" rowspan="2">Конструктив</th>
                  <th
                    v-for="(g, idx) in tier2MonthGroups"
                    :key="`month-${idx}-${g.monthKey}`"
                    class="month-head"
                    :colspan="g.dates.length"
                  >
                    {{ g.monthLabel }}
                  </th>
                </tr>
                <tr>
                  <th
                    v-for="d in tier2Dates"
                    :key="d.iso"
                    class="day-head"
                    :class="{ weekend: d.isWeekend }"
                  >
                    <div class="date-header-content">
                      <div class="day-number">{{ formatDateShort(d.iso) }}</div>
                      <div class="day-weekday">{{ getWeekday(d.iso) }}</div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr class="labor-row">
                  <td class="sticky-col labor-title">Выход людей</td>
                  <td
                    v-for="d in tier2Dates"
                    :key="`labor-${d.iso}`"
                    class="labor-cell"
                  >
                    <div class="labor-cell-grid">
                      <div class="labor-grid-cell labor-plan-cell">
                        {{ formatLaborValue(getLabor(d.iso).plan_people) }}
                      </div>
                      <div
                        class="labor-grid-cell labor-fact-cell"
                        :class="getLaborFactCellClass(getLabor(d.iso).plan_people, getLabor(d.iso).fact_people)"
                      >
                        {{ formatLaborValue(getLabor(d.iso).fact_people) }}
                      </div>
                    </div>
                  </td>
                </tr>

                <template v-for="block in blocks" :key="`block-${block.block_id}`">
                  <tr class="block-row">
                    <td class="sticky-col block-title" :colspan="1 + tier2Dates.length">
                      {{ block.block_name }}
                    </td>
                  </tr>

                  <tr
                    v-for="wg in workGroupsByBlock[block.block_id] || []"
                    :key="`wg-${block.block_id}-${wg.work_group_id}`"
                    class="wg-row"
                  >
                    <td class="sticky-col wg-title">{{ wg.work_group_name }}</td>
                    <td
                      v-for="d in tier2Dates"
                      :key="`cell-${wg.work_group_id}-${d.iso}`"
                      class="wg-cell"
                      :class="getDeviationCellClass(wg.work_group_id, d.iso)"
                    >
                      {{ formatDeviation(wg.work_group_id, d.iso) }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <!-- Таблица причин невыполнения плана (как в /tier-fact-timeline) -->
          <div v-if="filters.resident_id" class="reasons-section">
            <div class="reasons-toolbar">
              <span class="reasons-toolbar-title">Причины отставания</span>
              <q-toggle
                v-model="showAllReasons"
                label="Показать все (без привязки к периоду)"
                dense
                class="reasons-toggle"
                @update:model-value="loadTier2Data"
              />
            </div>
            <div v-if="tier2ReasonsGrouped.length === 0" class="no-reasons-data">
              Нет данных за выбранный период
            </div>
            <template v-for="group in tier2ReasonsGrouped" :key="`rg-${group.work_group_name}`">
              <div class="reasons-group-title">{{ group.work_group_name }}</div>
              <div class="reasons-table-wrapper">
                <table class="reasons-table">
                  <thead>
                    <tr>
                      <th class="reasons-table-header-number">№</th>
                      <th class="reasons-table-header-reason">Причины невыполнения плана на день</th>
                      <th
                        v-for="colNum in 20"
                        :key="`reasons-col-${group.work_group_name}-${colNum}`"
                        class="reasons-table-header-date"
                      >
                        {{ colNum }}
                      </th>
                      <th class="reasons-table-header-total">Итого</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(reason, reasonIdx) in group.reasons" :key="`r-${group.work_group_name}-${reason.reason_id}`">
                      <td class="reasons-table-cell-number">{{ reasonIdx + 1 }}</td>
                      <td class="reasons-table-cell-reason">{{ reason.reason_name }}</td>
                      <td
                        v-for="colNum in 20"
                        :key="`reasons-cell-${reason.reason_id}-${colNum}`"
                        class="reasons-table-cell-date"
                      >
                        {{ getReasonDateForColumn(reason, colNum) }}
                      </td>
                      <td
                        class="reasons-table-cell-total"
                        :class="getReasonTotalClass(reason)"
                      >
                        {{ getReasonTotal(reason) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </q-tab-panel>

        <!-- Tier 3 -->
        <q-tab-panel :name="3">
          <div class="tier3-toolbar">
            <div class="tier3-toolbar-left">
              <q-btn
                flat
                dense
                round
                icon="chevron_left"
                size="md"
                @click="tier3Shift(-1)"
                class="nav-btn"
                title="Предыдущий период"
              />
            </div>
            <div class="tier3-title">
              <div class="tier3-title-main">Tier 3</div>
              <div class="tier3-title-sub">{{ tier3MonthLabel }}</div>
            </div>
            <div class="tier3-toolbar-right">
              <q-btn
                flat
                dense
                round
                icon="refresh"
                size="sm"
                @click="loadTier3Data"
                class="nav-btn"
                title="Обновить данные"
                :loading="tier3Fetching"
                :disable="tier3Fetching"
              />
              <q-btn
                flat
                dense
                round
                icon="chevron_right"
                size="md"
                @click="tier3Shift(1)"
                class="nav-btn"
                title="Следующий период"
              />
            </div>
          </div>

          <div v-if="tier3Fetching" class="loading-msg">Загрузка...</div>

          <div v-else-if="tier3Projects.length === 0" class="no-data">
            Нет данных по проектам за выбранный период
          </div>

          <div v-else class="tier3-table-wrapper">
            <table class="tier3-table">
              <thead>
                <tr>
                  <th class="sticky-col head-project" rowspan="2">Проект</th>
                  <th
                    v-for="(g, idx) in tier3MonthGroups"
                    :key="`t3-month-${idx}-${g.monthKey}`"
                    class="month-head"
                    :colspan="g.dates.length"
                  >
                    {{ g.monthLabel }}
                  </th>
                </tr>
                <tr>
                  <th
                    v-for="d in tier3Dates"
                    :key="`t3-day-${d.iso}`"
                    class="day-head"
                    :class="{ weekend: d.isWeekend }"
                  >
                    <div class="date-header-content">
                      <div class="day-number">{{ d.day }}</div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- Выход людей по всем проектам (сверху) -->
                <tr class="labor-row labor-row-total">
                  <td class="sticky-col labor-title labor-title-total">Выход людей (всего)</td>
                  <td
                    v-for="d in tier3Dates"
                    :key="`t3-total-labor-${d.iso}`"
                    class="labor-cell"
                  >
                    <div class="labor-cell-grid">
                      <div class="labor-grid-cell labor-plan-cell">
                        {{ getTier3TotalLaborPlan(d.iso) }}
                      </div>
                      <div
                        class="labor-grid-cell labor-fact-cell"
                        :class="getTier3TotalLaborFactClass(d.iso)"
                      >
                        {{ getTier3TotalLaborFact(d.iso) }}
                      </div>
                    </div>
                  </td>
                </tr>
                <template v-for="project in tier3Projects" :key="`t3-prj-${project.sbs_project_id}`">
                  <!-- Название проекта -->
                  <tr class="project-header-row">
                    <td class="sticky-col project-name-cell" :colspan="tier3Dates.length + 1">
                      {{ project.sbs_project_name }}
                    </td>
                  </tr>
                  <!-- Блоки проекта -->
                  <tr
                    v-for="block in project.blocks"
                    :key="`t3-blk-${block.block_id}`"
                    class="block-row"
                  >
                    <td class="sticky-col block-name-cell">{{ block.block_name }}</td>
                    <td
                      v-for="d in tier3Dates"
                      :key="`t3-dev-${block.block_id}-${d.iso}`"
                      class="deviation-cell"
                      :class="getTier3DeviationClass(project.sbs_project_id, block.block_id, d.iso)"
                    >
                      {{ getTier3Deviation(project.sbs_project_id, block.block_id, d.iso) }}
                    </td>
                  </tr>
                  <!-- Общее отклонение по проекту (min по всем блокам) -->
                  <tr class="total-deviation-row">
                    <td class="sticky-col total-deviation-title">Общее отклонение</td>
                    <td
                      v-for="d in tier3Dates"
                      :key="`t3-total-dev-${project.sbs_project_id}-${d.iso}`"
                      class="deviation-cell"
                      :class="getTier3ProjectMinDeviationClass(project.sbs_project_id, d.iso)"
                    >
                      {{ getTier3ProjectMinDeviation(project.sbs_project_id, d.iso) }}
                    </td>
                  </tr>
                  <!-- Выход людей по проекту -->
                  <tr class="labor-row">
                    <td class="sticky-col labor-title">Выход людей</td>
                    <td
                      v-for="d in tier3Dates"
                      :key="`t3-labor-${project.sbs_project_id}-${d.iso}`"
                      class="labor-cell"
                    >
                      <div class="labor-cell-grid">
                        <div class="labor-grid-cell labor-plan-cell">
                          {{ getTier3LaborPlan(project.sbs_project_id, d.iso) }}
                        </div>
                        <div
                          class="labor-grid-cell labor-fact-cell"
                          :class="getTier3LaborFactClass(project.sbs_project_id, d.iso)"
                        >
                          {{ getTier3LaborFact(project.sbs_project_id, d.iso) }}
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <!-- Причины отставания Tier 3 (суммарно по всем проектам) -->
          <div class="reasons-section">
            <div class="reasons-toolbar">
              <span class="reasons-toolbar-title">Причины отставания</span>
              <q-toggle
                v-model="showAllReasons3"
                label="Показать все (без привязки к периоду)"
                dense
                class="reasons-toggle"
                @update:model-value="loadTier3Data"
              />
            </div>
            <template v-for="group in tier3ReasonsGrouped" :key="`t3r-wg-${group.work_group_name}`">
              <div class="reasons-group-title">{{ group.work_group_name }}</div>
              <div class="reasons-table-wrapper">
                <table class="reasons-table">
                  <thead>
                    <tr>
                      <th class="reasons-table-header-number">№</th>
                      <th class="reasons-table-header-reason">Причины невыполнения плана на день</th>
                      <th
                        v-for="colNum in 20"
                        :key="`t3r-col-${group.work_group_name}-${colNum}`"
                        class="reasons-table-header-date"
                      >
                        {{ colNum }}
                      </th>
                      <th class="reasons-table-header-total">Итого</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(reason, reasonIdx) in group.reasons" :key="`t3r-${group.work_group_name}-${reason.reason_id}`">
                      <td class="reasons-table-cell-number">{{ reasonIdx + 1 }}</td>
                      <td class="reasons-table-cell-reason">{{ reason.reason_name }}</td>
                      <td
                        v-for="colNum in 20"
                        :key="`t3r-cell-${reason.reason_id}-${colNum}`"
                        class="reasons-table-cell-date"
                      >
                        {{ getReasonDateForColumn(reason, colNum) }}
                      </td>
                      <td
                        class="reasons-table-cell-total"
                        :class="getReasonTotalClass(reason)"
                      >
                        {{ getReasonTotal(reason) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <div v-if="tier3ReasonsGrouped.length === 0" class="no-data">
              Нет причин отставания за выбранный период
            </div>
          </div>
        </q-tab-panel>

        <q-tab-panel v-for="tier in [4, 5, 6]" :key="`panel-${tier}`" :name="tier">
          <div class="stub">Tier {{ tier }} — форма в разработке.</div>
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </div>
</template>

<script>
import CustomSelect from "@/components/form/CustomSelect";
import CollapseBlock from "@/components/common/CollapseBlock";
import FiltersWrapper from "@/components/common/FiltersWrapper";
import { getResidents } from "@/views/TierFactFirst/services";
import { tier2GetGprDeviation, tier2GetLaborSum, tier2GetReasons, tier3GetDeviation, tier3GetLaborSum, tier3GetReasons } from "./services";
import { PAGES_AIDAR } from "@/constants/index_aidar";

export default {
  name: "TierFactTimelineTier2to6",
  components: { CustomSelect, CollapseBlock, FiltersWrapper },
  data() {
    return {
      tiers: [2, 3, 4, 5, 6],
      activeTier: 2,
      residents: [],
      residentsFetching: false,
      blocks: [],
      blocksFetching: false,
      workGroupsByBlock: {},
      tier2StartDate: null,
      tier2DeviationRows: [],
      tier2LaborRows: [],
      tier2ReasonsData: [],
      showAllReasons: true,
      // Tier 3
      tier3StartDate: null,
      tier3DeviationRows: [],
      tier3LaborRows: [],
      tier3ReasonsData: [],
      tier3Fetching: false,
      showAllReasons3: true,
      filters: {
        resident_id: "",
        sbs_project_id: "",
      },
    };
  },
  async mounted() {
    this.tier2StartDate = this.getDefaultTier2StartDate();
    this.tier3StartDate = this.getDefaultTier2StartDate(); // Та же логика половин месяца
    await this.loadResidents();
    await this.loadFiltersFromURL();
    // Tier 3 не зависит от выбора ЖК — загружаем сразу
    await this.loadTier3Data();
  },
  computed: {
    tier2Dates() {
      // Показываем 1-15 или 16-конец месяца
      const base = this.tier2StartDate ? new Date(this.tier2StartDate) : new Date();
      const year = base.getFullYear();
      const month = base.getMonth();
      const dayOfMonth = base.getDate();

      let startDay, endDay;
      if (dayOfMonth <= 15) {
        // Первая половина: 1-15
        startDay = 1;
        endDay = 15;
      } else {
        // Вторая половина: 16 - конец месяца
        startDay = 16;
        endDay = new Date(year, month + 1, 0).getDate(); // последний день месяца
      }

      const out = [];
      for (let day = startDay; day <= endDay; day++) {
        const d = new Date(year, month, day);
        const iso = this.toISO(d);
        out.push({ iso, day: d.getDate(), isWeekend: this.isWeekendISO(iso) });
      }
      return out;
    },
    tier2MonthGroups() {
      const groups = [];
      let current = null;
      this.tier2Dates.forEach((d) => {
        const dt = new Date(d.iso);
        const monthKey = `${dt.getFullYear()}-${dt.getMonth() + 1}`;
        const monthLabel = dt.toLocaleDateString("ru-RU", {
          month: "long",
          year: "numeric",
        });
        if (!current || current.monthKey !== monthKey) {
          current = { monthKey, monthLabel: this.capitalize(monthLabel), dates: [] };
          groups.push(current);
        }
        current.dates.push(d);
      });
      return groups;
    },
    tier2MonthLabel() {
      const base = this.tier2StartDate ? new Date(this.tier2StartDate) : new Date();
      return base.toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
    },
    tier2ReasonsGrouped() {
      // Группируем причины по группам работ
      // Данные приходят: work_group_name, reason_id, reason_name, dates (строка "08.11, 09.11")
      const groups = {};
      (this.tier2ReasonsData || []).forEach((r) => {
        if (!groups[r.work_group_name]) {
          groups[r.work_group_name] = { work_group_name: r.work_group_name, reasons: [] };
        }
        groups[r.work_group_name].reasons.push({
          reason_id: r.reason_id,
          reason_name: r.reason_name,
          dates: r.dates || "",
        });
      });
      // Преобразуем в массив и сортируем
      return Object.values(groups)
        .map((g) => ({
          work_group_name: g.work_group_name,
          reasons: g.reasons.sort((a, b) =>
            String(a.reason_name || "").localeCompare(String(b.reason_name || ""))
          ),
        }))
        .sort((a, b) => String(a.work_group_name || "").localeCompare(String(b.work_group_name || "")));
    },
    // ========== Tier 3 computed ==========
    tier3Dates() {
      const base = this.tier3StartDate ? new Date(this.tier3StartDate) : new Date();
      const year = base.getFullYear();
      const month = base.getMonth();
      const dayOfMonth = base.getDate();

      let startDay, endDay;
      if (dayOfMonth <= 15) {
        startDay = 1;
        endDay = 15;
      } else {
        startDay = 16;
        endDay = new Date(year, month + 1, 0).getDate();
      }

      const out = [];
      for (let day = startDay; day <= endDay; day++) {
        const d = new Date(year, month, day);
        const iso = this.toISO(d);
        out.push({ iso, day: d.getDate(), isWeekend: this.isWeekendISO(iso) });
      }
      return out;
    },
    tier3MonthGroups() {
      const groups = [];
      let current = null;
      this.tier3Dates.forEach((d) => {
        const dt = new Date(d.iso);
        const monthKey = `${dt.getFullYear()}-${dt.getMonth() + 1}`;
        const monthLabel = dt.toLocaleDateString("ru-RU", {
          month: "long",
          year: "numeric",
        });
        if (!current || current.monthKey !== monthKey) {
          current = { monthKey, monthLabel: this.capitalize(monthLabel), dates: [] };
          groups.push(current);
        }
        current.dates.push(d);
      });
      return groups;
    },
    tier3MonthLabel() {
      const base = this.tier3StartDate ? new Date(this.tier3StartDate) : new Date();
      return base.toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
    },
    tier3TotalLabor() {
      // Суммарный план/факт по всем проектам за каждую дату
      const byDate = {};
      (this.tier3LaborRows || []).forEach((r) => {
        if (!r.labor_date) return;
        if (!byDate[r.labor_date]) {
          byDate[r.labor_date] = { plan: 0, fact: 0 };
        }
        byDate[r.labor_date].plan += r.plan_sum || 0;
        byDate[r.labor_date].fact += r.fact_sum || 0;
      });
      return byDate;
    },
    tier3Projects() {
      // Строим иерархию: проекты → блоки
      const projectsMap = {};
      (this.tier3DeviationRows || []).forEach((r) => {
        if (!projectsMap[r.sbs_project_id]) {
          projectsMap[r.sbs_project_id] = {
            sbs_project_id: r.sbs_project_id,
            sbs_project_name: r.sbs_project_name,
            blocksMap: {},
          };
        }
        if (!projectsMap[r.sbs_project_id].blocksMap[r.block_id]) {
          projectsMap[r.sbs_project_id].blocksMap[r.block_id] = {
            block_id: r.block_id,
            block_name: r.block_name,
          };
        }
      });
      return Object.values(projectsMap)
        .map((p) => ({
          sbs_project_id: p.sbs_project_id,
          sbs_project_name: p.sbs_project_name,
          blocks: Object.values(p.blocksMap).sort((a, b) =>
            String(a.block_name || "").localeCompare(String(b.block_name || ""))
          ),
        }))
        .sort((a, b) => String(a.sbs_project_name || "").localeCompare(String(b.sbs_project_name || "")));
    },
    tier3ReasonsGrouped() {
      // Группируем причины только по группам работ (суммарно по всем проектам)
      // Данные: work_group_name, reason_id, reason_name, dates
      const groups = {};
      (this.tier3ReasonsData || []).forEach((r) => {
        if (!groups[r.work_group_name]) {
          groups[r.work_group_name] = { work_group_name: r.work_group_name, reasons: [] };
        }
        groups[r.work_group_name].reasons.push({
          reason_id: r.reason_id,
          reason_name: r.reason_name,
          dates: r.dates || "",
        });
      });
      return Object.values(groups)
        .map((g) => ({
          work_group_name: g.work_group_name,
          reasons: g.reasons.sort((a, b) =>
            String(a.reason_name || "").localeCompare(String(b.reason_name || ""))
          ),
        }))
        .sort((a, b) => String(a.work_group_name || "").localeCompare(String(b.work_group_name || "")));
    },
  },
  methods: {
    getDefaultTier2StartDate() {
      // Начало текущей половины месяца: 1 или 16
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      const dayOfMonth = d.getDate();
      d.setDate(dayOfMonth <= 15 ? 1 : 16);
      return d;
    },
    toISO(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    capitalize(s) {
      if (!s) return "";
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    formatDateShort(isoDate) {
      // dd.mm
      if (!isoDate) return "";
      const dt = new Date(isoDate);
      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      return `${day}.${month}`;
    },
    getWeekday(isoDate) {
      if (!isoDate) return "";
      const dt = new Date(isoDate);
      const w = dt.toLocaleDateString("ru-RU", { weekday: "short" });
      // 'пн'/'пн.' в зависимости от браузера — нормализуем
      return String(w).replace(".", "");
    },
    isWeekendISO(isoDate) {
      if (!isoDate) return false;
      const dt = new Date(isoDate);
      const day = dt.getDay(); // 0=Sun, 6=Sat
      return day === 0 || day === 6;
    },
    async loadResidents() {
      this.residentsFetching = true;
      this.residents = await getResidents();
      this.residentsFetching = false;
    },
    async onResidentChange(value) {
      this.filters.resident_id = value;
      // В resident__tier_read значение resident_id фактически равно sbs_project_id
      this.filters.sbs_project_id = value;
      this.updateURLWithFilters();
      await this.loadTier2Data();
    },
    updateURLWithFilters() {
      const query = {};
      if (this.filters.resident_id) {
        query.resident_id = this.filters.resident_id;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    async loadFiltersFromURL() {
      const { resident_id } = this.$route.query;
      if (resident_id) {
        this.filters.resident_id = parseInt(resident_id);
        // В resident__tier_read значение resident_id фактически равно sbs_project_id
        this.filters.sbs_project_id = this.filters.resident_id;
      }
      if (this.filters.resident_id) {
        await this.loadTier2Data();
      }
    },
    async loadTier2Data() {
      if (!this.filters.resident_id) return;
      if (!this.filters.sbs_project_id) return;

      const date_begin = this.tier2Dates[0]?.iso;
      const date_end = this.tier2Dates[this.tier2Dates.length - 1]?.iso;

      // Для причин: если showAllReasons — передаём null, иначе — даты периода
      const reasonsDateBegin = this.showAllReasons ? null : date_begin;
      const reasonsDateEnd = this.showAllReasons ? null : date_end;

      this.blocksFetching = true;
      try {
        const [devRows, laborRows, reasonsRows] = await Promise.all([
          tier2GetGprDeviation(this.filters.sbs_project_id, date_begin, date_end),
          tier2GetLaborSum(this.filters.sbs_project_id, date_begin, date_end),
          tier2GetReasons(this.filters.sbs_project_id, reasonsDateBegin, reasonsDateEnd),
        ]);

        this.tier2DeviationRows = devRows || [];
        this.tier2LaborRows = laborRows || [];
        this.tier2ReasonsData = reasonsRows || [];

        // Строим список блоков и групп работ из tier2DeviationRows
        const blocksMap = {};
        const wgsMap = {};
        (this.tier2DeviationRows || []).forEach((r) => {
          if (!blocksMap[r.block_id]) {
            blocksMap[r.block_id] = { block_id: r.block_id, block_name: r.block_name };
          }
          if (!wgsMap[r.block_id]) wgsMap[r.block_id] = {};
          if (!wgsMap[r.block_id][r.work_group_id]) {
            wgsMap[r.block_id][r.work_group_id] = {
              work_group_id: r.work_group_id,
              work_group_name: r.work_group_name,
            };
          }
        });
        this.blocks = Object.values(blocksMap).sort((a, b) =>
          String(a.block_name || "").localeCompare(String(b.block_name || ""))
        );
        const wgByBlock = {};
        Object.keys(wgsMap).forEach((blockId) => {
          wgByBlock[blockId] = Object.values(wgsMap[blockId]).sort((a, b) =>
            String(a.work_group_name || "").localeCompare(String(b.work_group_name || ""))
          );
        });
        this.workGroupsByBlock = wgByBlock;
      } finally {
        this.blocksFetching = false;
      }
    },
    tier2Shift(direction) {
      // direction: -1 = назад, +1 = вперёд
      // Переключение между половинами месяца: 1-15 и 16-конец
      if (!this.filters.resident_id) return;
      const d = this.tier2StartDate ? new Date(this.tier2StartDate) : new Date();
      const year = d.getFullYear();
      const month = d.getMonth();
      const dayOfMonth = d.getDate();

      let newDate;
      if (direction < 0) {
        // Назад
        if (dayOfMonth <= 15) {
          // Сейчас 1-15 → переходим на 16+ предыдущего месяца
          newDate = new Date(year, month - 1, 16);
        } else {
          // Сейчас 16+ → переходим на 1-15 этого месяца
          newDate = new Date(year, month, 1);
        }
      } else {
        // Вперёд
        if (dayOfMonth <= 15) {
          // Сейчас 1-15 → переходим на 16+ этого месяца
          newDate = new Date(year, month, 16);
        } else {
          // Сейчас 16+ → переходим на 1-15 следующего месяца
          newDate = new Date(year, month + 1, 1);
        }
      }

      newDate.setHours(0, 0, 0, 0);
      this.tier2StartDate = newDate;
      this.loadTier2Data();
    },
    getDeviation(work_group_id, isoDate) {
      const key = `${work_group_id}_${isoDate}`;
      if (!this._tier2DeviationMap) this._tier2DeviationMap = {};
      if (this._tier2DeviationMap.__src !== this.tier2DeviationRows) {
        const map = {};
        (this.tier2DeviationRows || []).forEach((r) => {
          const d = r.work_date;
          const k = `${r.work_group_id}_${d}`;
          map[k] = {
            day_cnt: r.day_cnt,
            calc_comment: r.calc_comment,
          };
        });
        map.__src = this.tier2DeviationRows;
        this._tier2DeviationMap = map;
      }
      return this._tier2DeviationMap[key] || null;
    },
    formatDeviation(work_group_id, isoDate) {
      const v = this.getDeviation(work_group_id, isoDate);
      if (!v || v.day_cnt === null || v.day_cnt === undefined) return "";
      return v.day_cnt;
    },
    getDeviationCellClass(work_group_id, isoDate) {
      const v = this.getDeviation(work_group_id, isoDate);
      if (!v || v.day_cnt === null || v.day_cnt === undefined) return "";
      if (v.day_cnt < 0) return "neg";
      if (v.day_cnt > 0) return "pos";
      return "zero";
    },
    getDeviationTitle(work_group_id, isoDate) {
      const v = this.getDeviation(work_group_id, isoDate);
      return v?.calc_comment || "";
    },
    getLabor(isoDate) {
      if (!this._tier2LaborMap) this._tier2LaborMap = {};
      if (this._tier2LaborMap.__src !== this.tier2LaborRows) {
        const map = {};
        (this.tier2LaborRows || []).forEach((r) => {
          map[r.work_date] = {
            plan_people: r.plan_people ?? null,
            fact_people: r.fact_people ?? null,
          };
        });
        map.__src = this.tier2LaborRows;
        this._tier2LaborMap = map;
      }
      return this._tier2LaborMap[isoDate] || { plan_people: null, fact_people: null };
    },
    formatLaborValue(v) {
      return v === null || v === undefined || v === "" ? "—" : v;
    },
    getLaborFactCellClass(planPeople, factPeople) {
      if (
        planPeople === null ||
        planPeople === undefined ||
        planPeople === "" ||
        factPeople === null ||
        factPeople === undefined ||
        factPeople === ""
      ) {
        return "";
      }

      const plan = parseFloat(planPeople);
      const fact = parseFloat(factPeople);

      if (isNaN(plan) || isNaN(fact)) {
        return "";
      }

      if (fact >= plan) {
        return "labor-fact-green";
      }

      return "labor-fact-red";
    },
    getReasonDateForColumn(reason, columnNumber) {
      // reason.dates содержит строку вида "08.11, 09.11, 10.11"
      // Нужно распределить даты по колонкам: первая дата в колонку 1, вторая в колонку 2 и т.д.
      if (!reason.dates || !reason.dates.trim()) {
        return "";
      }

      // Разбиваем строку дат на массив
      const datesArray = reason.dates
        .split(", ")
        .map((d) => d.trim())
        .filter((d) => d);

      // Возвращаем дату для указанной колонки (колонки нумеруются с 1)
      const dateIndex = columnNumber - 1;

      if (dateIndex >= 0 && dateIndex < datesArray.length) {
        return datesArray[dateIndex];
      }

      return "";
    },
    getReasonTotal(reason) {
      // Подсчитываем общее количество отставаний
      // "08.11" = 1, "08.11 (3)" = 3
      if (!reason.dates || !reason.dates.trim()) {
        return 0;
      }

      const datesArray = reason.dates
        .split(", ")
        .map((d) => d.trim())
        .filter((d) => d);

      let total = 0;
      datesArray.forEach((dateStr) => {
        // Ищем число в скобках
        const match = dateStr.match(/\((\d+)\)/);
        if (match) {
          total += parseInt(match[1], 10);
        } else {
          total += 1;
        }
      });
      return total;
    },
    getReasonTotalClass(reason) {
      // Возвращает класс цвета в зависимости от количества
      const total = this.getReasonTotal(reason);
      if (total === 0) return "";
      if (total <= 2) return "total-level-1";  // светло-красный
      if (total <= 5) return "total-level-2";  // красный
      if (total <= 10) return "total-level-3"; // тёмно-красный
      if (total <= 20) return "total-level-4"; // очень тёмно-красный
      return "total-level-5";                   // чёрно-красный
    },
    // ========== Tier 3 methods ==========
    tier3Shift(direction) {
      const d = this.tier3StartDate ? new Date(this.tier3StartDate) : new Date();
      const year = d.getFullYear();
      const month = d.getMonth();
      const dayOfMonth = d.getDate();

      let newDate;
      if (direction < 0) {
        if (dayOfMonth <= 15) {
          newDate = new Date(year, month - 1, 16);
        } else {
          newDate = new Date(year, month, 1);
        }
      } else {
        if (dayOfMonth <= 15) {
          newDate = new Date(year, month, 16);
        } else {
          newDate = new Date(year, month + 1, 1);
        }
      }

      newDate.setHours(0, 0, 0, 0);
      this.tier3StartDate = newDate;
      this.loadTier3Data();
    },
    async loadTier3Data() {
      const date_begin = this.tier3Dates[0]?.iso;
      const date_end = this.tier3Dates[this.tier3Dates.length - 1]?.iso;

      // Для причин: если showAllReasons3 — передаём null
      const reasonsDateBegin = this.showAllReasons3 ? null : date_begin;
      const reasonsDateEnd = this.showAllReasons3 ? null : date_end;

      this.tier3Fetching = true;
      try {
        const [devRows, laborRows, reasonsRows] = await Promise.all([
          tier3GetDeviation(date_begin, date_end),
          tier3GetLaborSum(date_begin, date_end),
          tier3GetReasons(reasonsDateBegin, reasonsDateEnd),
        ]);
        this.tier3DeviationRows = devRows || [];
        this.tier3LaborRows = laborRows || [];
        this.tier3ReasonsData = reasonsRows || [];
      } catch (e) {
        console.error("Tier 3 load error:", e);
        this.tier3DeviationRows = [];
        this.tier3LaborRows = [];
        this.tier3ReasonsData = [];
      } finally {
        this.tier3Fetching = false;
      }
    },
    getTier3Deviation(sbs_project_id, block_id, isoDate) {
      const row = (this.tier3DeviationRows || []).find(
        (r) =>
          r.sbs_project_id === sbs_project_id &&
          r.block_id === block_id &&
          r.work_date === isoDate
      );
      return row?.deviation ?? "";
    },
    getTier3DeviationClass(sbs_project_id, block_id, isoDate) {
      const val = this.getTier3Deviation(sbs_project_id, block_id, isoDate);
      if (val === "" || val === null || val === undefined) return "";
      if (val < 0) return "neg";
      if (val > 0) return "pos";
      return "zero";
    },
    getTier3LaborPlan(sbs_project_id, isoDate) {
      const row = (this.tier3LaborRows || []).find(
        (r) => r.sbs_project_id === sbs_project_id && r.labor_date === isoDate
      );
      return row?.plan_sum ?? "";
    },
    getTier3LaborFact(sbs_project_id, isoDate) {
      const row = (this.tier3LaborRows || []).find(
        (r) => r.sbs_project_id === sbs_project_id && r.labor_date === isoDate
      );
      return row?.fact_sum ?? "";
    },
    getTier3LaborFactClass(sbs_project_id, isoDate) {
      const plan = this.getTier3LaborPlan(sbs_project_id, isoDate);
      const fact = this.getTier3LaborFact(sbs_project_id, isoDate);
      if (plan === "" || fact === "") return "";
      if (fact < plan) return "neg";
      return "pos";
    },
    getTier3TotalLaborPlan(isoDate) {
      return this.tier3TotalLabor[isoDate]?.plan || "";
    },
    getTier3TotalLaborFact(isoDate) {
      return this.tier3TotalLabor[isoDate]?.fact || "";
    },
    getTier3TotalLaborFactClass(isoDate) {
      const plan = this.tier3TotalLabor[isoDate]?.plan;
      const fact = this.tier3TotalLabor[isoDate]?.fact;
      if (!plan && !fact) return "";
      if (fact < plan) return "neg";
      return "pos";
    },
    getTier3ProjectMinDeviation(sbs_project_id, isoDate) {
      // Минимальное отклонение по всем блокам проекта за день
      const rows = (this.tier3DeviationRows || []).filter(
        (r) => r.sbs_project_id === sbs_project_id && r.work_date === isoDate
      );
      if (rows.length === 0) return "";
      const values = rows.map((r) => r.deviation).filter((v) => v !== null && v !== undefined);
      if (values.length === 0) return "";
      return Math.min(...values);
    },
    getTier3ProjectMinDeviationClass(sbs_project_id, isoDate) {
      const val = this.getTier3ProjectMinDeviation(sbs_project_id, isoDate);
      if (val === "" || val === null || val === undefined) return "";
      if (val < 0) return "neg";
      if (val > 0) return "pos";
      return "zero";
    },
    openReasonReport() {
      // Переход на страницу отчета с передачей resident_id
      this.$router.push({
        path: PAGES_AIDAR.tierReasonReport,
        query: {
          resident_id: this.filters.resident_id,
        },
      });
    },
  },
};
</script>

<style scoped>
.tier-fact-timeline-2-6-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filters-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.reason-report-icon-btn {
  margin-bottom: 2px;
}

.reason-report-icon-btn:hover {
  background: rgba(25, 118, 210, 0.08);
}

/* Убираем лишний нижний отступ у CollapseBlock (он зарезервирован под expand, но у нас alwaysHideExpand=true) */
.tier26-filters-collapse.collapseBlock {
  padding-bottom: 0 !important;
}

.content {
  background: #fff;
  border-radius: 8px;
  padding: 6px 12px;
}

.tabs-row {
  display: flex;
  align-items: center;
}

/* Вкладки Tier 2-6 как кнопки/пилюли */
:deep(.tier26-tabs) {
  gap: 8px;
  padding: 2px 0 6px 0;
}

:deep(.tier26-tabs .q-tabs__content) {
  gap: 8px;
}

:deep(.tier26-tabs .q-tab) {
  min-height: 30px;
  border-radius: 10px;
  padding: 0 14px;
  border: 1px solid #d0d0d0;
  background: #fff;
  color: #1f2937;
  font-weight: 600;
  text-transform: none;
}

:deep(.tier26-tabs .q-tab:hover) {
  background: #f6f8fa;
}

:deep(.tier26-tabs .q-tab--active) {
  background: var(--q-primary);
  border-color: var(--q-primary);
  color: #fff;
  box-shadow: 0 2px 8px rgba(2, 123, 227, 0.28);
}

:deep(.tier26-tabs .q-tab--active .q-tab__label) {
  color: #fff;
}

.stub {
  padding: 12px 0;
  color: #5c6b77;
}

.no-data {
  padding: 12px 0;
  color: #5c6b77;
}

.tier2-toolbar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 8px 0;
}

.tier2-toolbar-left {
  justify-self: start;
  display: flex;
  align-items: center;
}

.tier2-toolbar-right {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tier2-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.tier2-title-main {
  font-weight: 600;
}

.tier2-title-sub {
  font-size: 12px;
  color: #5c6b77;
  text-transform: capitalize;
}

.tier2-table-wrapper {
  overflow: auto;
  border: 1px solid #bdbdbd;
  border-radius: 8px;
}

.tier2-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 920px;
  background: #fff;
}

.tier2-table th,
.tier2-table td {
  border: 1px solid #bdbdbd;
  padding: 4px 6px;
  text-align: center;
  font-size: 12px;
  line-height: 1.2;
  vertical-align: middle;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  text-align: left !important;
  box-shadow: 1px 0 0 #bdbdbd;
}

.head-constructive {
  background: #ffeb3b;
  font-weight: 700;
  width: 360px;
  min-width: 360px;
}

.month-head {
  background: #ffe2a8;
  font-weight: 700;
  text-transform: capitalize;
}

.day-head {
  background: #ffe2a8;
  font-weight: 600;
}

.date-header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.1;
}

.day-number {
  font-weight: 700;
}

.day-weekday {
  font-size: 11px;
  color: #5c6b77;
}

.day-head.weekend {
  background: #ffd0d0;
}

.labor-title {
  font-weight: 700;
  background: #e8f5e9;
  color: #1b5e20;
  width: 360px;
  min-width: 360px;
}

.labor-row-total {
  background: #c8e6c9;
  border-bottom: 2px solid #388e3c;
}

.labor-title-total {
  background: #a5d6a7 !important;
  font-size: 13px;
}

.total-deviation-row {
  background: #fff8e1;
}

.total-deviation-title {
  font-weight: 700;
  background: #ffecb3 !important;
  color: #e65100;
  font-style: italic;
}

.labor-cell-grid {
  display: flex;
  height: 100%;
  width: 100%;
}

.labor-cell {
  background: #fff;
}

.labor-grid-cell {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  padding: 2px;
  border-right: 1px solid #d0d0d0;

  &:last-child {
    border-right: none;
  }
}

.labor-plan-cell {
  background: #eeeeee;
  color: #1976d2; /* синий как на таймлайне для плановой части */
}

.labor-fact-cell {
  background: #e8f5e9;
  color: #388e3c;
}

.labor-fact-cell.labor-fact-green {
  background: #c8e6c9;
  color: #1b5e20;
}

.labor-fact-cell.labor-fact-red {
  background: #ffcdd2;
  color: #b71c1c;
}

.block-row .block-title {
  background: #ffe2a8;
  font-weight: 700;
}

.wg-title {
  background: #fff;
  width: 360px;
  min-width: 360px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.15;
  padding-top: 6px;
  padding-bottom: 6px;
}

.wg-cell.pos,
.labor-cell.pos {
  background: #a5d6a7;
}

.wg-cell.zero,
.labor-cell.zero {
  background: #c8e6c9;
}

.wg-cell.neg,
.labor-cell.neg {
  background: #ef9a9a;
}

/* Таблица причин (как в /tier-fact-timeline) */
.reasons-section {
  margin-top: 24px;
}

.reasons-toolbar {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 2px solid #ffe2a8;
}

.no-reasons-data {
  padding: 16px;
  text-align: center;
  color: #999;
  font-style: italic;
}

.reasons-toolbar-title {
  font-weight: 700;
  font-size: 15px;
  color: #c75000;
}

.reasons-toggle {
  font-size: 13px;
}

.reasons-project-title {
  font-weight: 700;
  font-size: 15px;
  color: #1565c0;
  padding: 14px 0 6px 0;
  border-bottom: 3px solid #bbdefb;
  margin-top: 16px;
  background: #e3f2fd;
  padding-left: 8px;
  margin-left: -8px;
  margin-right: -8px;
  padding-right: 8px;
}

.reasons-group-title {
  font-weight: 700;
  font-size: 14px;
  color: #c75000;
  padding: 10px 0 6px 0;
  border-bottom: 2px solid #ffe2a8;
  margin-bottom: 0;
}

.reasons-table-wrapper {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.reasons-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #fff;
}

.reasons-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.reasons-table th {
  background: #fff3cd;
  border: 1px solid #d0d0d0;
  padding: 8px 4px;
  text-align: center;
  font-weight: 600;
  font-size: 11px;
  white-space: nowrap;
}

.reasons-table td {
  border: 1px solid #d0d0d0;
  padding: 6px 4px;
  text-align: center;
  vertical-align: middle;
}

.reasons-table-header-number {
  min-width: 40px;
  max-width: 40px;
  width: 40px;
  background: #fff3cd;
}

.reasons-table-header-reason {
  min-width: 180px;
  max-width: 220px;
  width: 200px;
  background: #fff3cd;
  text-align: left !important;
  padding-left: 8px;
}

.reasons-table-header-date {
  min-width: 42px;
  max-width: 42px;
  width: 42px;
  background: #fff3cd;
}

.reasons-table-cell-number {
  min-width: 40px;
  max-width: 40px;
  width: 40px;
  background: #f9f9f9;
  font-weight: 600;
}

.reasons-table-cell-reason {
  min-width: 180px;
  max-width: 220px;
  width: 200px;
  text-align: left !important;
  padding-left: 8px;
  background: #fff;
  white-space: normal;
  word-break: break-word;
}

.reasons-table-cell-date {
  min-width: 42px;
  max-width: 42px;
  width: 42px;
  background: #fff;
  color: #d32f2f;
  font-weight: 600;
}

.reasons-table-header-total {
  min-width: 50px;
  max-width: 50px;
  width: 50px;
  background: #e0e0e0;
  font-weight: 700;
}

.reasons-table-cell-total {
  min-width: 50px;
  max-width: 50px;
  width: 50px;
  font-weight: 700;
  background: #f5f5f5;
}

/* Уровни красности для итого */
.reasons-table-cell-total.total-level-1 {
  background: #ffcdd2;
  color: #c62828;
}

.reasons-table-cell-total.total-level-2 {
  background: #ef9a9a;
  color: #b71c1c;
}

.reasons-table-cell-total.total-level-3 {
  background: #e57373;
  color: #fff;
}

.reasons-table-cell-total.total-level-4 {
  background: #c62828;
  color: #fff;
}

.reasons-table-cell-total.total-level-5 {
  background: #7f0000;
  color: #fff;
}

/* ========== Tier 3 styles ========== */
.tier3-toolbar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 8px 0;
}

.tier3-toolbar-left {
  display: flex;
  justify-content: flex-start;
}

.tier3-toolbar-right {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

.tier3-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.tier3-title-main {
  font-weight: 600;
}

.tier3-title-sub {
  font-size: 12px;
  color: #5c6b77;
  text-transform: capitalize;
}

.loading-msg {
  padding: 12px 0;
  color: #5c6b77;
}

.tier3-table-wrapper {
  overflow: auto;
  border: 1px solid #bdbdbd;
  border-radius: 8px;
}

.tier3-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  table-layout: fixed;
}

.tier3-table th,
.tier3-table td {
  border: 1px solid #d0d0d0;
  text-align: center;
  vertical-align: middle;
  padding: 4px 6px;
}

.tier3-table .sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
}

/* Важно: sticky-col задаёт белый фон, поэтому для названия проекта/блока переопределяем */
.tier3-table .sticky-col.project-name-cell {
  background: #f3f4f6 !important;
}

.tier3-table .sticky-col.block-name-cell {
  background: #fffde7 !important;
}

.tier3-table .head-project {
  width: 200px;
  min-width: 200px;
  background: #fff3cd;
  font-weight: 700;
  text-align: left;
  padding-left: 8px;
}

.project-header-row {
  background: #f3f4f6;
}

.project-name-cell {
  font-weight: 700;
  text-align: left;
  padding-left: 8px;
  background: #f3f4f6;
  color: inherit;
  border-top: 2px solid #d1d5db;
  border-bottom: 2px solid #d1d5db;
  font-size: 14px;
}

.block-row {
  background: #fff;
}

.block-name-cell {
  width: 200px;
  min-width: 200px;
  text-align: left;
  padding-left: 16px;
  font-style: italic;
  background: #fffde7;
}

.deviation-cell {
  min-width: 38px;
  font-weight: 600;
  font-size: 12px;
}

.deviation-cell.neg {
  background: #ef9a9a;
  color: #b71c1c;
}

.deviation-cell.pos {
  background: #a5d6a7;
  color: #1b5e20;
}

.deviation-cell.zero {
  background: #c8e6c9;
  color: #2e7d32;
}
</style>


